import { convertDomainArrayToMap, getInstituteFlags, getStoredUserInstituteIds, isEmpty, toSentenceCase } from '../../../library/Core/SmartFunctions';
import { axiosClient } from '../../../library/Service/axiosClient';
import DomainDataService from '../../../services/domain-data.service';
import PageConfigService from '../../../services/page-config.service';
import StudentService from '../../../services/student.service';
import { reportConfig } from './report-config';
import PageConfigAllStudentReport  from './PageConfigAllStudentReport.json';

class ManageAlumniService {
    static pageLoader(params: any): Promise<any> {
        const state: any = {};
        const searchCriteria = ManageAlumniService.getDefaultSearchCriteria(params.pageName);

         const institutes = getStoredUserInstituteIds();

        // Skip search in the loader by default — Layout/SearchCriteria fetches on mount,
        // so calling search() here causes a double API call. Pass deferList: false explicitly
        // on any route that strictly needs pre-fetched list data.
        const searchPromise = params.deferList !== false
            ? Promise.resolve({ data: [] })
            : ManageAlumniService.search(searchCriteria, params.pageName);

        return Promise.all([
            this.getPageConfig(params.pageName),
            searchPromise,
            ManageAlumniService.getSchoolSearchFilterDomains(params.pageName),
            StudentService.getProgramDomainData(params.pageName),
            DomainDataService.get(),
        ]).then((values) => {
            state.formConfig = (params.pageName==='internal-admin-all-student-report') ? values[0] : values[0]?.data;
            if (params.pageName === 'track-alumni-profiles') {
                state.formConfig = ManageAlumniService.injectMembershipFeeFilter(state.formConfig);
            }
            state.routeInfo = params;
            state.domain = values[4].data instanceof Map ? values[4].data : convertDomainArrayToMap(values[4].data);
            state.internal = { gridState: {}, programDomainData: values[3].data };

            const { engagementHeader } = getInstituteFlags(state,institutes)
            searchCriteria.isEngagementHeader = engagementHeader;

            state.data = {
                alumniList: values[1].data ?? [],
                isListLoading: !!params.deferList,
                // searchCriteria: { ...searchCriteria, schoolId: sessionStorage.getItem('user-school-id') },
                searchCriteria: { ...searchCriteria },
                filterDomains: values[2].data,
                reportConfig: reportConfig[params.pageName],
            };
            return Promise.resolve(state);
        });
    }

     static getPageConfig(pageName:string) {
        if (pageName === 'internal-admin-all-student-report') {
            return Promise.resolve(PageConfigAllStudentReport);
        }else{
            const res = PageConfigService.get(pageName);
            return res;
        }
    }

    static injectMembershipFeeFilter(formConfig: any) {
        if (!formConfig?.sectionRepository) return formConfig;
        const updated = JSON.parse(JSON.stringify(formConfig));
        const section = updated.sectionRepository.find((s: any) => s.id === 'searchCriteria');
        if (!section?.controlGroup) return updated;
        if (section.controlGroup.some((c: any) => c.id === 'membershipFeePaymentStatusCode')) return updated;

        const filterControl = {
            id: 'membershipFeePaymentStatusCode',
            type: 'MULTI_SELECT_CUSTOM_CONTROL_2',
            props: {
                label: 'Membership Fees Payment Status',
                customProperties: {},
                domainCategoryCode: 'MEMBERSHIP_FEE_PAYMENT_STATUS_CODE',
            },
            className: 'col-md-3',
        };
        const meterIds = ['almaMaterPride', 'marketability', 'vibrancy', 'donorScore', 'mentorshipScore', 'recruiterScore'];
        const insertAt = section.controlGroup.findIndex((c: any) => meterIds.includes(c.id));
        if (insertAt >= 0) {
            section.controlGroup.splice(insertAt, 0, filterControl);
        } else {
            section.controlGroup.push(filterControl);
        }
        return updated;
    }


    static search(searchCriteria: any, reportType: string): any {
        const payload = ManageAlumniService.sanitizeSearchCriteria(searchCriteria, reportType);
        return axiosClient().post(`${process.env.REACT_APP_REPORTS_SERVER}/management/manage-alumni`, payload);
    }

    // Notes: This is key function in forming the search criteria for the report. Handle with care.
    static sanitizeSearchCriteria(searchCriteria: any, reportType: string) {
        const reportTypesToRemoveDates = [
            'track-student-career-preferences',
            'track-alumni-education-preferences',
            'track-alumni-engagement-with-students',
            'track-alumni-highlights-with-students-engagement',
        ];
        
        if (reportTypesToRemoveDates.includes(reportType)) {
            delete searchCriteria.createdFrom;
            delete searchCriteria.createdTo;
        }

        if (reportType != 'track-alumni-engagement-with-students') {
            delete searchCriteria.conversationFromDate;
            delete searchCriteria.conversationToDate;
        }

        const schoolIdFromSession = sessionStorage.getItem('user-school-id') == 'undefined' ? null : sessionStorage.getItem('user-school-id');
        if (reportType.includes('student')) {
            searchCriteria = { ...searchCriteria, schoolId: schoolIdFromSession, userTypeCode: 'STUDENT' };
            // if(reportType == 'track-student-profiles') delete searchCriteria.schoolId;
            if (['track-student-profiles','internal-admin-all-student-report'].includes(reportType)) 
            delete searchCriteria.schoolId;

        }

        if (reportType.includes('alumni')) {
            searchCriteria = { ...searchCriteria, schoolId: schoolIdFromSession, userTypeCode: 'ALUMNI' };
        }

        if (['YES', 'NO'].includes(searchCriteria['workingStatusCode'])) {
            searchCriteria['workingStatusCode'] = searchCriteria['workingStatusCode'] === 'YES' ? 1 : 0;
        }

        if (['YES', 'NO'].includes(searchCriteria['isNotable'])) {
            searchCriteria['isNotable'] = searchCriteria['isNotable'] === 'YES' ? 1 : 0;
        }

        if (['YES', 'NO'].includes(searchCriteria['isRaw'])) {
            searchCriteria['isRaw'] = searchCriteria['isRaw'] === 'YES' ? 1 : 0;
        }

        if (['YES', 'NO'].includes(searchCriteria['isEmailVerified'])) {
            searchCriteria['isEmailVerified'] = searchCriteria['isEmailVerified'] === 'YES' ? 1 : 0;
        }

        if (['YES', 'NO'].includes(searchCriteria['isSemiVerified'])) {
            searchCriteria['isSemiVerified'] = searchCriteria['isSemiVerified'] === 'YES' ? 1 : 0;
        }

        ['connected', 'engaged', 'recognized'].forEach((key) => {
        const val = searchCriteria[key];
        if (val === 'YES') {
            searchCriteria[key] = 'Yes';
        } else if (val === 'NO') {
            searchCriteria[key] = 'No';
        } else if(val=== 'Not Available'){
           searchCriteria[key] = 'Not Available';
        }else{
            delete searchCriteria[key]; // Remove if not explicitly set
        }
        });

    
        // Alumni Track reports follow the server-side per-institute "Show Only Active
        // Alumni profiles" toggle, so do NOT force ACTIVE here. Student (and other
        // non-alumni) non-profile reports keep their existing ACTIVE-only behaviour.
        if (!reportType.includes('profile') && !reportType.includes('alumni')) {
            searchCriteria = { ...searchCriteria, userStatusCode: 'ACTIVE' };
        }

        if (searchCriteria.hasOwnProperty('notUpdated') && searchCriteria?.notUpdated) {
            searchCriteria = { ...searchCriteria, [`notUpdated${toSentenceCase(searchCriteria?.notUpdated)}`]: 1 };
        }

        if (searchCriteria.hasOwnProperty('notSure') && searchCriteria?.notSure) {
            searchCriteria = { ...searchCriteria, [`notSure${toSentenceCase(searchCriteria?.notSure)}`]: 1 };
        }

        if (reportType == 'track-alumni-engagement-with-students') {
            searchCriteria = { ...searchCriteria, hasStudentEngagement: 'TRUE' };
        }

        if (!isEmpty(searchCriteria.campusCode)) {
            delete searchCriteria.schoolId;
        }
        return searchCriteria;
    }

    static getSchoolSearchFilterDomains(reportName: string = '') {
        switch (reportName) {
            case 'track-alumni-engagement-with-students':
                return axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-filters`);
            case 'track-alumni-profiles':
             return  axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-filters`,{ params: { applyUserStatus: 1 } });
            case 'track-alumni-highlights-with-students-engagement':
                return axiosClient().get(
                    `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/school-engagement-search-filter-alumni-student`
                );
            case 'track-student-career-preferences':
                return axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-student-filters`);
            case 'track-student-profiles':
            case 'internal-admin-all-student-report' :
                return axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-student-filters`);
            default:
                return axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-filters`);
        }
    }

    static deactivateStudent = async (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/deactivate`, { students });
    };

    static activateStudent = (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/activate`, { students });
    };

    static emailVerifyStudent = (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/emailVerify`, { students });
    };

    static markAsNotable = (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/mark-notable`, { students });
    };

    static unMarkAsNotable = (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/unmark-notable`, { students });
    };

    static convertToAlumni = (students: any) => {
        console.log(students, ' students 119');
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/convertToAlumni`, { students });
    };

    static deleteUser = (students: any) => {
        return axiosClient().post(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/school-directory/delete-user`, { students });
    };

    static getDefaultSearchCriteria(pageName: string = ''): any {
        let additionalCriteria = {};
        const criteria = {
            // return {
            passOutYear: [],
            // classCode: [],
            // curriculumCode: [],
            campusCode: [],
            programId: [],
            educationOrEmploymentCurrentStatusCode: [],
            countryCode: [],
            cityCode: [],
            residingCityCode: [],
            residingCountryCode: [],
            collegeCountryCode: [],

            preferenceCountryCode: [],
            countryPreferenceOrder: null,
            preferenceFieldOfStudy: [],
            careerPreferenceOrder: null,

            collegeCityCode: [],
            fieldOfStudyCode: [],
            notableCode: [],
            instituteId: [],
            pursuingEducationLevelCode: [],
            workingStatusCode:[],
            isNotable: null,
            isRaw : "NO",
            isSemiVerified :null,
            isEmailVerified : "YES",
            profileCompletionPercentage : [],
            almaMaterPride : [],
            marketability:[],
            vibrancy:[],
            donorScore :[],
            mentorshipScore : [],
            recruiterScore : [],
            engaged:[],
            recognized:[],
            connected:[],
            membershipFeePaymentStatusCode: [],


            // createdFrom: moment().subtract(3, 'months').format('YYYY-MM-DD'),
            // createdTo: moment().format('YYYY-MM-DD'),

            // conversationFromDate: moment().subtract(3, 'months').format('YYYY-MM-DD'),
            // conversationToDate: moment().format('YYYY-MM-DD'),
        };

        if (['track-student-profiles', 'track-alumni-profiles','internal-admin-all-student-report'].includes(pageName)) {
            additionalCriteria = { userStatusCode: 'ACTIVE' };
        }

        return { ...criteria, ...additionalCriteria };
    }
}

export default ManageAlumniService;
